import React from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useChatStore } from '../../store/chatStore';

interface DeleteThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  threadId?: string;
  threadTitle?: string;
}

const DeleteThreadModal: React.FC<DeleteThreadModalProps> = ({
  isOpen,
  onClose,
  threadId,
  threadTitle,
}) => {
  const { deleteThread } = useChatStore();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleDelete = async () => {
    if (!threadId) return;
    
    setIsLoading(true);
    try {
      await deleteThread(threadId);
      onClose();
    } catch (error) {
      // Error handled by toast in store
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Thread" maxWidth="sm">
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="flex-1">
            <p className="text-gray-700">
              Are you sure you want to delete the thread{' '}
              <strong>"{threadTitle || 'this conversation'}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone. All messages in this conversation will be permanently deleted.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            icon={Trash2}
            onClick={handleDelete}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete Thread'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteThreadModal;
